import { EventSourcePolyfill } from 'event-source-polyfill'
import mitt from 'mitt'
import { toast } from 'sonner'

import { API_URL } from '@/constants/env'

import { buildSearchUrl } from '@/helpers/search/url'

import type {
  SearchRequest,
  SearchResponse,
  SearchStore,
  StoreGetter,
  StoreSetter,
} from '@/types/Search'

export const ABORT_SEARCH: string = 'abortSearch'

export const searchEmitter = mitt()

export const setRequest = async (
  request: SearchRequest,
  set: StoreSetter,
  get: StoreGetter,
  accessToken?: string | null,
) => {
  if (accessToken === undefined) {
    return
  }

  // abort any previous event source streams
  searchEmitter.emit(ABORT_SEARCH)

  if (request.offset === undefined) {
    set((state: SearchStore) => ({
      ...state,
      augmentedQueries: [],
      request,
      isStreaming: true,
      steps: [],
      summary: '',
      // only show the loading indicator if there's a query
      summaryIsLoading: request.query.length > 0,
      data: undefined,
      dataIsLoading: true,
    }))
  } else {
    set((state: SearchStore) => ({
      ...state,
      paginationIsLoading: true,
    }))
  }

  const url = buildSearchUrl(request)
  const eventSource = new EventSourcePolyfill(`${API_URL}/api/v1${url}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    heartbeatTimeout: 60000,
  })

  // this is for initial errors that happen before the stream begins but after the request was sent
  eventSource.addEventListener('error', (event) => {
    const status = (event as unknown as { status?: number }).status

    // for some reason, an error fires on every request, even if the request is successful,
    // so only surface auth failures
    if (status === 401) {
      toast.error(
        "We couldn't verify your sign-in. Refresh the page and try again.",
        {
          id: 'search-sign-in',
        },
      )
    } else if (status === 403) {
      toast.error('You are not authorized to perform this action.')
    }
  })

  const closeEventSource = () => {
    try {
      eventSource.close()
    } catch (e) {
      // ignore errors during close
    } finally {
      searchEmitter.off(ABORT_SEARCH, closeEventSource)
    }
  }

  searchEmitter.on(ABORT_SEARCH, closeEventSource)

  eventSource.onmessage = (event) => {
    const response: SearchResponse = JSON.parse(event.data)

    if (response.type === 'data' && response.data) {
      set((state: SearchStore) => ({
        ...state,
        data:
          state.data !== undefined
            ? {
                ...state.data,
                data: [...state.data.data, ...(response.data?.data ?? [])],
                has_more: response.data?.has_more ?? false,
              }
            : response.data,
        dataIsLoading: false,
        paginationIsLoading: false,
      }))
    }

    if (response.type === 'augmented_queries' && response.augmented_queries) {
      set((state: SearchStore) => ({
        ...state,
        augmentedQueries: response.augmented_queries,
      }))
    }

    if (response.type === 'gen_response' && response.gen_response) {
      set((state: SearchStore) => ({
        ...state,
        summary: state.summary + response.gen_response,
        summaryIsLoading: false,
      }))
    }

    // typescript doesn't know that the search_step is not undefined
    const step = response.search_step
    if (response.type === 'search_step' && step !== undefined) {
      set((state: SearchStore) => ({
        ...state,
        steps: [...(state.steps ?? []), step],
      }))
    }

    if (response.type === 'error') {
      toast.error('Something went wrong. Please try again later.')
      set((state: SearchStore) => ({
        ...state,
        dataIsLoading: false,
        summaryIsLoading: false,
        isStreaming: false,
        paginationIsLoading: false,
      }))
      closeEventSource()
    }
  }

  eventSource.onerror = (error) => {
    // this gets triggered at the end of the run to indicate the search request has finished processing;
    // not sure if there's a better way to do it but it works for now
    set((state: SearchStore) => ({
      ...state,
      dataIsLoading: false,
      summaryIsLoading: false,
      isStreaming: false,
    }))
    closeEventSource()
  }
}
